import React, { useState, useEffect, lazy } from 'react';
import { FormattedDate, useIntl, defineMessages, FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import classNames from 'classnames';
import { Link as ScrollLink } from 'react-scroll';
import { Link } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import { fetchProduct } from 'Api/Product/product-service';
import { fetchPublisherSection } from 'Redux/ducks/algolia-calls';
import { ageRatingBoard, currencyCode } from 'Helpers/Geo/data';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import ShowcaseCriticReviews from 'Features/Product/components/ShowcaseCriticReviews/ShowcaseCriticReviews';
import ShowcaseEditions from 'Features/Product/components/ShowcaseEditions/ShowcaseEditions';
import ShowcaseGallery from 'Features/Product/components/ShowcaseGallery/ShowcaseGallery';
import ShowcaseCarousel from 'Features/Product/components/ShowcaseCarousel/ShowcaseCarousel';
import ShowcaseDetails from 'Features/Product/components/ShowcaseDetails/ShowcaseDetails';
import useViewport from 'Hooks/useViewport';
import imgixConfig from 'Config/imgix';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import VideoImgLoader from 'Components/Utility/Video/VideoImgLoader';
import WishlistButton from 'Components/WishlistButton/WishlistButton';
import ProductSEOHelmet from 'Components/Utility/SEO/ProductSEOHelmet';
import NotFound from 'Components/NotFound/NotFound';
import ProductCover from 'Components/Utility/ProductCover';
import AgeGatingContainer from 'Components/Utility/Age/AgeGatingContainer';
import ProductHeaderPrices from 'Features/Product/components/ProductHeader/ProductHeaderPrices';
import ProductAddToCartBtn from 'Components/ProductAddToCartBtn/ProductAddToCartBtn';
import env from 'Env';

import 'Features/Product/pages/ShowcaseProduct.scss';

const ProductOffers = lazy(() => import(/* webpackChunkName: "ProductOffers" */ 'Components/ProductOffers/ProductOffers'));
const ProductFAQs = lazy(() => import(/* webpackChunkName: "ProductAboutExtras" */ 'Features/Product/components/ProductAboutSection/ProductFAQs'));
const AutoFAQs = lazy(() => import(/* webpackChunkName: "ProductAboutExtras" */ 'Features/Product/components/ProductAboutSection/AutoFAQs'));

const messages = defineMessages({
  other_games: {
    id: 'showcase.product.title.other_games',
    defaultMessage: 'Other games by {name}',
  },
});

export default function ShowcaseProduct({ match }) {
  const [product, setProduct] = useState(null);
  const [publisherHits, setPublisherHits] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aboutCollapsed, setAboutCollapsed] = useState(true);
  const [visible, setVisible] = useState([]);

  const intl = useIntl();
  const { formatMessage } = intl;

  const { isAboveMd } = useViewport();

  const slug = match.params.slug;

  // Get Google Font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=PT+Sans+Narrow:wght@400;700&display=swap';
    link.type = 'text/css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  });

  // Fetch product by slug or product previews by ID
  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      setNotFound(false);

      return fetchProduct(slug)
        .then((result) => {
          if (result.notFound) {
            setTimeout(() => {
              window.prerenderReady = true;
            }, 1500);
            return setNotFound(true);
          }

          const foundProduct = result.product;

          setIsLoading(false);
          return setProduct(foundProduct);
        }).catch(setFetchError);
    };

    if (slug) {
      loadProduct();
    } else {
      setNotFound(true);
    }
  }, [slug]);

  const passedAgeGate = useSelector(state => state?.ageGating?.success);

  const name = get(product, 'name', '');
  const productId = get(product, '_id', '');
  const screenshots = get(product, 'img', []);
  let backgroundSrc = get(screenshots, [0], {});
  if (slug === 'indiana-jones-and-the-great-circle') {
    backgroundSrc = get(screenshots, [1], {});
  }
  const publishers = get(product, 'publishers', []);
  const publisher = get(publishers, [0], {});
  const releaseDate = get(product, 'release_date', '');
  const shortDescription = get(product, 'seo.desc', '');
  const cover = get(product, 'cover', '');
  const videos = get(product, 'video', []);
  const description = get(product, 'desc', '');
  const faqs = get(product, 'faqs', []);
  const metaCritic = get(product, 'metaCritic', {});
  const openCritic = get(product, 'openCritic', {});
  const metaCriticScore = get(metaCritic, 'score', 0);
  const openCriticScore = get(openCritic, 'score', 0);
  const editions = get(product, 'editions', []);
  const categories = get(product, 'categories', []);
  const themes = get(product, 'themes', []);
  const playstyles = get(product, 'playstyles', []);
  const ageRatings = get(product, 'age', {});
  const presale = get(product, 'presale', false);
  const freeToPlay = get(product, 'free_to_play', false);
  const giveaway = get(product, 'giveaway', false);
  const payWhatYouWant = get(product, 'pay_what_you_want', false);
  const soldOut = get(product, 'sold_out', false);
  const regionLocked = get(product, 'region_locked', false);
  const currentPrice = get(product, 'currentPrice', {});

  const languagesCheck = ProductRequirements.LanguagesCheck(product);
  const noGermanRelease = languagesCheck.noGermanRelease;

  const tags = [];
  categories.forEach((category) => {
    if (!some(tags, { name: category.name })) tags.push(category);
  });
  themes.forEach((theme) => {
    if (!some(tags, { name: theme.name })) tags.push(theme);
  });
  playstyles.forEach((playstyle) => {
    if (!some(tags, { name: playstyle.name })) tags.push(playstyle);
  });

  // Get publisher
  useEffect(() => {
    const editionSlugs = [slug];
    if (!isEmpty(editions)) {
      editions.forEach((edition) => {
        if (!editionSlugs.includes(edition.slug)) {
          editionSlugs.push(edition.slug);
        }
      });
    }

    const fetchHits = async () => {
      const newHits = await fetchPublisherSection(publisher.name, editionSlugs);
      setPublisherHits(newHits.hits);
    };

    fetchHits().catch(setFetchError);
  }, [publisher, slug, editions]);

  if (fetchError) {
    return (
      <div>There has been an error</div>
    );
  }

  const changeVisibility = (isVisible, type) => {
    if (isVisible) {
      setVisible([...visible, type]);
    }
  };

  const outNow = Date.parse(releaseDate) < Date.now();

  const recommendedThreshold = env.percentRecommendedThreshold;
  const showMetaCritic = metaCriticScore >= recommendedThreshold && get(metaCritic, 'reviews.length', 0) >= 3;
  const showOpenCritic = openCriticScore >= recommendedThreshold && get(openCritic, 'reviews.length', 0) >= 3;

  const showCriticReviewSection = showMetaCritic || showOpenCritic;

  // Check age gating

  const ageGateThresholds = {
    ESRB: 17,
    USK: 16,
    PEGI: 18,
    ACB: 18,
  };
  let ageGateRequired = false;
  if (!passedAgeGate && ageRatings[ageRatingBoard] >= ageGateThresholds[ageRatingBoard]) {
    ageGateRequired = true;
  }

  const slideWidths = {
    xs: 300,
    sm: 350,
    md: 350,
    lg: 400,
    xl: 358,
    xxl: 420,
  };

  const singleEdition = isEmpty(editions);

  const showSoldOutButton = soldOut && !giveaway && !freeToPlay && !presale;

  let showBigWishListButton = (presale || noGermanRelease || soldOut) && !freeToPlay && !giveaway;
  const today = new Date();
  if (
    !presale &&
    releaseDate &&
    (new Date(releaseDate) > today) &&
    product.availability &&
    product.availability.valid_from &&
    (new Date(product.availability.valid_from) > today)
  ) {
    showBigWishListButton = true;
  }

  const price = currentPrice[currencyCode];

  if (notFound) return <NotFound />;


  return (
    <main className={classNames('ShowcaseProduct', { 'ShowcaseProduct--ageGated': ageGateRequired })}>
      <ProductSEOHelmet product={product} />
      {isLoading &&
        <section className="ShowcaseProduct__loading">
          <div className="ShowcaseProduct__loading__icon">
            <img src="https://cdn.fanatical.com/production/brand/fanatical-icon-white.svg" alt="Loading Page" />
          </div>
        </section>
      }

      {!isLoading && product &&
        <section className="ShowcaseProduct__main">
          <div className="ShowcaseProduct__top">
            <div className="ShowcaseProduct__top__foreground">
              <div className="ShowcaseProduct__top__foreground__content container">
                <div className="ShowcaseProduct__top__foreground__content__publisher">
                  {publisher.name}
                </div>
                <div className="ShowcaseProduct__top__foreground__content__name">
                  {name}
                </div>
                {releaseDate &&
                  <div className="ShowcaseProduct__top__foreground__content__release">
                    {outNow ? 'Released' : 'Coming'} <FormattedDate value={Date.parse(releaseDate)} year="numeric" month="long" day="2-digit" />
                  </div>
                }
                <div className="ShowcaseProduct__top__foreground__content__description">
                  {shortDescription}
                </div>
                {!singleEdition &&
                  <div className="ShowcaseProduct__top__foreground__content__prices">
                    <div className="PriceContainer">
                      <div className="PriceContainer__price">
                        <FormattedMessage
                          id="showcase.product.price"
                          defaultMessage="From {price}"
                          values={{
                            price: <SpecialFormattedNumber value={price / 100} style="currency" />,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                }
                {singleEdition && !regionLocked &&
                  <div className="ShowcaseProduct__top__foreground__content__prices">
                    <ProductHeaderPrices product={product} />
                  </div>
                }
                <div className="ShowcaseProduct__top__foreground__content__buttons">
                  <div className="ShowcaseProduct__top__foreground__content__buttons__group">
                    {!singleEdition &&
                      <ScrollLink
                        spy
                        smooth
                        offset={-100}
                        activeClass="active"
                        to="ShowcaseEditions"
                        duration={2000}
                        className="ShowcaseButton ShowcaseButton--primary"
                      >
                        <FormattedMessage id="showcase.product.button.buy-now" defaultMessage="View editions" />
                      </ScrollLink>
                    }

                    {singleEdition && showSoldOutButton &&
                      <button disabled className="ShowcaseButton ShowcaseButton--primary">
                        <FormattedMessage id="showcase.product.button.sold-out" defaultMessage="Sold out" />
                      </button>
                    }

                    {showBigWishListButton &&
                      <WishlistButton
                        plainButton
                        slug={slug}
                        product={product}
                        productId={productId}
                        className="ShowcaseButton ShowcaseButton--outline"
                      />
                    }

                    {singleEdition && !soldOut && !presale && !freeToPlay && !payWhatYouWant &&
                      <ProductAddToCartBtn product={product} showStickyHeader={false} />
                    }

                    {!showBigWishListButton &&
                      <WishlistButton
                        iconButton
                        slug={slug}
                        product={product}
                        productId={productId}
                        className="ShowcaseButton ShowcaseButton--circle"
                      />
                    }
                  </div>
                  {metaCriticScore > 0 && isAboveMd &&
                    <div className="ShowcaseProduct__top__foreground__content__buttons__metacritic">
                      <div className="ShowcaseProduct__top__foreground__content__buttons__metacritic__logo">
                        <img src="https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-icon.svg" alt="Metacritic" />
                      </div>
                      <div className="ShowcaseProduct__top__foreground__content__buttons__metacritic__score">
                        {metaCriticScore}
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="ShowcaseProduct__content">
            <div className="ShowcaseProduct__content__trailer container">
              <div className="ShowcaseHeading">
                <FormattedMessage id="showcase.product.title.trailer" defaultMessage="Trailer" />
              </div>
              {!ageGateRequired && !isEmpty(videos) &&
                <VideoImgLoader
                  cover={cover}
                  video={videos[0]}
                  sizesOverride="(min-width: 1520px) 1324px, (min-width: 1200px) 1124px, (min-width: 991px) 944px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
                />
              }
              {(ageGateRequired || isEmpty(videos)) &&
                <div className="ShowcaseProduct__content__trailer__cover">
                  <ProductCover
                    cover={cover}
                    alt={`Product cover for ${name}`}
                    sizesOverride="(min-width: 1520px) 1324px, (min-width: 1200px) 1124px, (min-width: 991px) 944px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
                  />
                  {ageGateRequired &&
                    <AgeGatingContainer ratings={ageRatings} lazyLoad={false} className="ShowcaseProduct__content__trailer__cover__ageGating" />
                  }
                </div>
              }
            </div>

            <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'about'); }}>
              <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('about') })}>
                <div className="ShowcaseAboutDetails container">
                  <div className="ShowcaseAbout">
                    <div className="ShowcaseHeading">
                      <FormattedMessage
                        id="showcase.product.title.about"
                        defaultMessage="About {name}"
                        values={{
                          name,
                        }}
                      />
                    </div>

                    {!isEmpty(tags) &&
                      <div className="ShowcaseAbout__tags">
                        {tags.slice(0, 10).map((tag) => {
                          if (typeof tag === 'string') return (<div key={tag} className="ShowcaseAbout__tags__tag">{tag}</div>);

                          if (!tag.link) {
                            return (
                              <div key={tag.name} className="ShowcaseAbout__tags__tag">
                                {tag.name}
                              </div>
                            );
                          }

                          return (
                            <Link to={`/${tag.facet}/${tag.slug}`} key={tag._id} className="ShowcaseAbout__tags__tag">
                              {tag.name}
                            </Link>
                          );
                        },
                        )}
                      </div>
                    }

                    <div className="ShowcaseAbout__textContainer">
                      <DangerousHTML
                        html={description}
                        className={classNames('ShowcaseAbout__text', { 'ShowcaseAbout__text--collapsed': aboutCollapsed })}
                      />
                      {aboutCollapsed &&
                        <div className="ShowcaseAbout__textContainer__gradient" />
                      }
                    </div>
                    {aboutCollapsed &&
                      <div className="ShowcaseAbout__buttonContainer">
                        <button className="ShowcaseAbout__button" onClick={() => { setAboutCollapsed(false); }}>
                          <FormattedMessage id="showcase.product.about.button.more" defaultMessage="Show more" />
                        </button>
                      </div>
                    }
                  </div>

                  <ShowcaseDetails product={product} />
                </div>
              </section>
            </InView>

            {!ageGateRequired &&
              <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'gallery'); }}>
                <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('gallery') })}>
                  <ShowcaseGallery
                    product={product}
                  />
                </section>
              </InView>
            }

            <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'offers'); }}>
              <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('offers') })}>
                <div className="container">
                  <ProductOffers product={product} />
                </div>
              </section>
            </InView>

            {window.locale === 'en' && showCriticReviewSection &&
              <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'critic'); }}>
                <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('critic') })}>
                  <ShowcaseCriticReviews
                    product={product}
                  />
                </section>
              </InView>
            }

            <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'editions'); }}>
              <ShowcaseEditions
                visible={visible}
                product={product}
              />
            </InView>

            <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'faqs'); }}>
              <div className={classNames('ShowcaseProduct__content__faqs container', { FadeInSection: visible.includes('faqs') })}>
                {product && faqs && !!faqs.length ?
                  <ProductFAQs newStyle faqs={faqs} />
                  :
                  <AutoFAQs newStyle product={product} />
                }
              </div>
            </InView>

            {!isEmpty(publisherHits) &&
              <InView threshold={0.5} onChange={(e) => { changeVisibility(e, 'publisher'); }}>
                <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('publisher') })}>
                  <div className="ShowcaseProduct__publisherGames">
                    <ShowcaseCarousel
                      slideWidths={slideWidths}
                      title={formatMessage(messages.other_games, { name: publisher.name })}
                      appearsOn={'publisher'}
                    >
                      {publisherHits.slice(0, 3).map(game =>
                        <Link to={`/showcase/${game.slug}`} className="ShowcaseProduct__publisherGames__card" key={game.name}>
                          <img
                            src={`${env.cdnUrl}/product/original/${game.cover}?${imgixConfig}`}
                            alt={game.name}
                          />
                        </Link>,
                      )}
                    </ShowcaseCarousel>
                  </div>
                </section>
              </InView>
            }
          </div>

          <div className="ShowcaseProduct__main__background">
            <div className="ShowcaseProduct__main__background__topGradient" />
            <div className="ShowcaseProduct__main__background__bottomGradient" />
            <div className="ShowcaseProduct__main__background__overlay" />
            <div className="ShowcaseProduct__main__background__container">
              <img
                srcSet={`
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=1280&h=720&fit=crop 1280w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=1130&h=636&fit=crop 1130w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=960&h=540&fit=crop 960w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=740&h=416&fit=crop 740w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=640&h=360&fit=crop 640w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=480&h=270&fit=crop 480w,
                  ${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=384&h=216&fit=crop 384w
                `}
                src={`${env.cdnUrl}/product/original/${backgroundSrc.slug}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w`}
                sizes="100vw"
                alt=""
                className="ShowcaseProduct__main__background__container__img"
              />
            </div>
          </div>
        </section>
      }
    </main>
  );
}
